/**
 * Que es lo que viaja, del lado de la pantalla. Logica pura: sin DOM y sin red.
 *
 * El mismo criterio que `services/api/src/carga.ts`, y por eso la validacion de
 * aqui NO es la validacion: la API la repite y la base la repite otra vez como
 * CHECK [migracion 0033]. Esto evita el viaje de ida y vuelta y marca QUE campo
 * falta; lo que no puede es dejar entrar algo que los otros dos rechacen, y por
 * eso las reglas se escriben en el mismo orden y con las mismas palabras.
 *
 * ===========================================================================
 * CAMBIAR DE NATURALEZA NO ARRASTRA LOS CAMPOS DE LA ANTERIOR
 *
 * `cuerpoDeCarga` manda SOLO lo que corresponde a la naturaleza elegida. Sin
 * eso, alguien que rellena una temperatura, cambia a PELIGROSA y manda, deja
 * una temperatura fantasma en el correo al proveedor --y el proveedor cotiza un
 * reefer que nadie pidio--. Los campos se conservan en el formulario a
 * proposito, para que volver atras no cueste teclearlos otra vez; lo que no
 * viaja es lo que no aplica.
 */

export type Naturaleza = 'GENERAL' | 'REFRIGERADA' | 'PELIGROSA' | 'SOBREDIMENSIONADA';

export const NATURALEZAS: readonly Naturaleza[] = [
  'GENERAL',
  'REFRIGERADA',
  'PELIGROSA',
  'SOBREDIMENSIONADA',
];

export interface FormularioCarga {
  readonly naturaleza: Naturaleza;
  readonly temperaturaC: string;
  readonly imoClase: string;
  readonly imoUn: string;
  readonly largoCm: string;
  readonly anchoCm: string;
  readonly altoCm: string;
  readonly nota: string;
}

export const CARGA_VACIA: FormularioCarga = {
  naturaleza: 'GENERAL',
  temperaturaC: '',
  imoClase: '',
  imoUn: '',
  largoCm: '',
  anchoCm: '',
  altoCm: '',
  nota: '',
};

/**
 * Los campos que faltan para la naturaleza elegida.
 *
 * Se devuelven los NOMBRES de los campos, no un mensaje: quien pinta decide si
 * marca el borde, escribe una linea o las dos cosas. Es el mismo contrato que
 * `armarBusqueda` y `armarCotizacion` [peticiones.ts].
 */
export function faltanDeCarga(f: FormularioCarga): string[] {
  const faltan: string[] = [];
  const vacio = (v: string): boolean => v.trim() === '';

  if (f.naturaleza === 'REFRIGERADA' && vacio(f.temperaturaC)) {
    faltan.push('temperatura_c');
  }
  if (f.naturaleza === 'PELIGROSA') {
    // Los dos: la clase dice el tratamiento y el UN dice la sustancia, y
    // ninguno sustituye al otro.
    if (vacio(f.imoClase)) faltan.push('imo_clase');
    if (vacio(f.imoUn)) faltan.push('imo_un');
  }
  if (f.naturaleza === 'SOBREDIMENSIONADA') {
    // Las tres: con dos no se puede decidir si cabe.
    if (vacio(f.largoCm)) faltan.push('dim_largo_cm');
    if (vacio(f.anchoCm)) faltan.push('dim_ancho_cm');
    if (vacio(f.altoCm)) faltan.push('dim_alto_cm');
  }
  return faltan;
}

/**
 * Lo que viaja en la peticion.
 *
 * Solo lo de la naturaleza elegida. Ver la cabecera: un dato de la naturaleza
 * anterior acaba en el correo al proveedor.
 *
 * La nota SI viaja siempre, incluso con GENERAL: es texto libre del agente y
 * puede decir algo que ninguna de las cuatro naturalezas cubre.
 */
export function cuerpoDeCarga(f: FormularioCarga): Record<string, unknown> {
  const nota = f.nota.trim();
  const base: Record<string, unknown> = {
    naturaleza: f.naturaleza,
    ...(nota === '' ? {} : { naturaleza_nota: nota }),
  };

  if (f.naturaleza === 'REFRIGERADA') {
    return { ...base, temperatura_c: f.temperaturaC.trim() };
  }
  if (f.naturaleza === 'PELIGROSA') {
    return { ...base, imo_clase: f.imoClase.trim(), imo_un: f.imoUn.trim() };
  }
  if (f.naturaleza === 'SOBREDIMENSIONADA') {
    return {
      ...base,
      dim_largo_cm: f.largoCm.trim(),
      dim_ancho_cm: f.anchoCm.trim(),
      dim_alto_cm: f.altoCm.trim(),
    };
  }
  return base;
}

/**
 * Si conviene proponer equipos reefer.
 *
 * Es la UNICA deduccion que esta pantalla hace sobre el catalogo a partir de la
 * carga, y la unica que no puede estar mal: una carga refrigerada no viaja en
 * un contenedor seco. No filtra --los demas equipos siguen en la lista, porque
 * un NOR es un reefer usado como seco y hay quien lo elige a proposito-- solo
 * los pone delante.
 *
 * Todo lo demas --descartar tarifas de peligrosa, comprobar que las medidas
 * quepan-- necesita datos que el catalogo no tiene, y sin ellos el filtro se
 * inventaria el criterio. Un buscador que esconde tarifas por una regla
 * inventada es peor que uno que las ensena todas.
 */
export function sugiereReefer(f: FormularioCarga): boolean {
  return f.naturaleza === 'REFRIGERADA';
}

/** Un equipo es de frio si su codigo lo dice. `40NOR` incluido: es un reefer,
 *  aunque se use como seco. */
export function esEquipoDeFrio(codigo: string): boolean {
  return /RF|NOR/i.test(codigo);
}
